import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Bus, Route, RefreshCw, CalendarDays, Home, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { canAccessCentroDiurno } from "../../utils/centroDiurnoAccess";
import { getGiri, generaGiro, ottimizzaGiro, getMezzi, getAutisti } from "../../utils/centroDiurnoApi";
import PageHeader from "../../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import CdRouteMap from "../../components/centro-diurno/CdRouteMap";

const FASCE = [
  { id: "mattina", label: "Mattina", orario: "08:00–13:00" },
  { id: "pomeriggio", label: "Pomeriggio", orario: "13:00–18:00" },
];

const DIREZIONI = [
  { id: "andata", label: "Andata", desc: "Raccolta ospiti", icon: ArrowUpRight },
  { id: "rientro", label: "Rientro", desc: "Accompagnamento a casa", icon: Home },
];

const STATO_GIRO_LABELS = {
  programmato: "Programmato",
  in_viaggio: "In viaggio",
  completato: "Completato",
};

const STATO_FERMATA_LABELS = {
  in_attesa: "In attesa",
  salito: "Salito",
  sceso: "Sceso a casa",
  assente: "Assente",
  ritardo: "Ritardo",
};

function findGiro(giri, fascia, direzione) {
  return giri.find((g) => g.fascia === fascia && (g.direzione || "andata") === direzione) || null;
}

function SegmentGroup({ options, value, onChange, className = "" }) {
  return (
    <div className={`inline-flex rounded-lg bg-slate-100/90 p-1 border border-slate-200/80 ${className}`}>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`rounded-md px-3 py-2 text-left transition-all ${
            value === opt.id
              ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          <span className="block text-sm font-medium leading-none">{opt.label}</span>
          {opt.orario && <span className="block text-[10px] mt-1 opacity-70">{opt.orario}</span>}
          {opt.desc && !opt.orario && (
            <span className="block text-[10px] mt-1 opacity-70 hidden sm:block">{opt.desc}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export default function CentroDiurnoTrasporti() {
  const { token, user, hasPermission, activeStruttura } = useAuth();
  const [dataSel, setDataSel] = useState(format(new Date(), "yyyy-MM-dd"));
  const [fascia, setFascia] = useState("mattina");
  const [direzione, setDirezione] = useState("andata");
  const [giri, setGiri] = useState([]);
  const [autisti, setAutisti] = useState([]);
  const canManage = hasPermission("centro_diurno:manage");

  const fasciaMeta = FASCE.find((f) => f.id === fascia) || FASCE[0];
  const dirMeta = DIREZIONI.find((d) => d.id === direzione) || DIREZIONI[0];
  const giro = findGiro(giri, fascia, direzione);
  const DirIcon = dirMeta.icon;

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await getGiri(token, dataSel);
      setGiri(res.giri || []);
    } catch (e) {
      toast.error("Errore caricamento giri");
    }
  }, [token, dataSel, activeStruttura?.id]);

  useEffect(() => {
    if (canAccessCentroDiurno(user)) load();
  }, [load, user]);

  useEffect(() => {
    if (!token || !canAccessCentroDiurno(user)) return;
    getAutisti(token)
      .then((res) => setAutisti(res.autisti || []))
      .catch(() => setAutisti([]));
  }, [token, user, activeStruttura?.id]);

  const autistaNome = (id) => {
    const a = autisti.find((x) => String(x._id || x.id) === String(id));
    return a ? `${a.nome || ""} ${a.cognome || ""}`.trim() : null;
  };

  // In Drive ogni autista vede solo i giri assegnati a lui: l'assegnazione usa il campo
  // autista_id già previsto da "genera" (rigenera il giro della fascia con l'autista scelto).
  const giroBloccato = giro && ["in_viaggio", "completato"].includes(giro.stato);
  const handleAssegnaAutista = (autistaId) => handleGenera(autistaId || "");

  const handleFasciaChange = (id) => {
    setFascia(id);
    setDirezione(id === "pomeriggio" ? "rientro" : "andata");
  };

  const handleGenera = async (autistaId) => {
    try {
      const mezzi = await getMezzi(token);
      const m = mezzi.mezzi?.find((x) => x.id === giro?.mezzo_id) || mezzi.mezzi?.[0];
      if (!m) {
        toast.error("Configura un mezzo prima");
        return;
      }
      const body = { data: dataSel, fascia, direzione, mezzo_id: m.id };
      if (typeof autistaId === "string") body.autista_id = autistaId;
      const result = await generaGiro(token, body);
      const n = result?.fermate?.length ?? 0;
      const skipped = result?.skipped?.length ?? 0;
      const dirLabel = direzione === "rientro" ? "rientro" : "andata";
      if (n > 0) {
        toast.success(
          skipped
            ? `Giro ${dirLabel} con ${n} fermate (${skipped} esclusi)`
            : `Giro ${dirLabel} con ${n} fermate`
        );
      } else {
        toast.warning(
          skipped
            ? `Giro senza fermate: ${skipped} ospiti esclusi (verifica indirizzi)`
            : "Giro senza fermate: nessun ospite idoneo"
        );
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const handleOttimizza = async () => {
    if (!giro?.id) return;
    try {
      await ottimizzaGiro(token, giro.id);
      toast.success("Percorso ottimizzato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ottimizzazione fallita");
    }
  };

  if (!canAccessCentroDiurno(user)) {
    return <div className="p-8 text-center text-slate-600">Modulo non attivo</div>;
  }

  const statoLabel = giro ? STATO_GIRO_LABELS[giro.stato] || giro.stato || "Programmato" : null;
  const dataLabel = format(parseISO(dataSel), "EEEE d MMMM yyyy", { locale: it });

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
        <PageHeader
          title="Trasporti e Pulmino"
          subtitle="Pianifica raccolta e rientro a domicilio per mattina e pomeriggio"
          icon={Bus}
          iconContainerClassName="bg-gradient-to-br from-emerald-500 to-emerald-700"
          iconClassName="text-white"
        />

        <Card className="shadow-sm border-slate-200 overflow-hidden">
          {/* Barra filtri integrata */}
          <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 px-4 py-4 md:px-6 md:py-5 space-y-4">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="data-trasporti" className="text-xs text-slate-500 uppercase tracking-wide">
                  Giorno
                </Label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600 pointer-events-none" />
                    <Input
                      id="data-trasporti"
                      type="date"
                      value={dataSel}
                      onChange={(e) => setDataSel(e.target.value)}
                      className="w-auto pl-9 bg-white border-slate-200"
                    />
                  </div>
                  <span className="text-sm text-slate-600 capitalize">{dataLabel}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {giri.length > 0 && (
                  <Badge variant="secondary" className="bg-white/80">
                    {giri.length} {giri.length === 1 ? "percorso" : "percorsi"} in giornata
                  </Badge>
                )}
                <Button variant="outline" size="sm" onClick={load} className="bg-white/80">
                  <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
                </Button>
              </div>
            </div>

            <div className="flex flex-col xl:flex-row xl:items-center gap-3 xl:gap-6">
              <div className="space-y-1.5">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Fascia oraria</p>
                <SegmentGroup options={FASCE} value={fascia} onChange={handleFasciaChange} />
              </div>
              <div className="hidden xl:block w-px h-10 bg-slate-200 shrink-0" aria-hidden />
              <div className="space-y-1.5">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Tipo percorso</p>
                <SegmentGroup options={DIREZIONI} value={direzione} onChange={setDirezione} />
              </div>
            </div>
          </div>

          {/* Intestazione percorso selezionato */}
          <div className="px-4 md:px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <DirIcon className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-900">
                  {dirMeta.label} — {fasciaMeta.label}
                </h2>
                <p className="text-sm text-slate-500">{dirMeta.desc} · {fasciaMeta.orario}</p>
              </div>
            </div>
            {giro && (
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <Badge className={giro.stato === "completato" ? "bg-slate-600" : "bg-emerald-600"}>
                  {statoLabel}
                </Badge>
                <span className="text-sm text-slate-500">
                  {giro.fermate?.length || 0} fermate · {Math.round((giro.distanza_m || 0) / 1000)} km
                  {giro.mezzo_nome ? ` · ${giro.mezzo_nome}` : ""}
                </span>
                {canManage ? (
                  <label className="flex items-center gap-1.5 text-sm text-slate-600">
                    <span>Autista</span>
                    <select
                      data-testid="giro-autista-select"
                      value={giro.autista_id || ""}
                      onChange={(e) => handleAssegnaAutista(e.target.value)}
                      disabled={giroBloccato}
                      title={giroBloccato ? "Giro già avviato: autista non modificabile" : undefined}
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800"
                    >
                      <option value="">Nessuno (non visibile in Drive)</option>
                      {autisti.map((a) => (
                        <option key={a._id || a.id} value={a._id || a.id}>
                          {`${a.nome || ""} ${a.cognome || ""}`.trim() || a.email}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <span className="text-sm text-slate-500" data-testid="giro-autista">
                    Autista: {autistaNome(giro.autista_id) || "non assegnato"}
                  </span>
                )}
                {canManage && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleOttimizza}>
                      <Route className="w-4 h-4 mr-1" /> Ottimizza
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleGenera()}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Rigenera
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          <CardContent className="p-4 md:p-6">
            {!giro ? (
              <div className="text-center py-12 px-4 rounded-xl bg-slate-50 border border-dashed border-slate-200">
                <Bus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700">
                  Nessun percorso {dirMeta.label.toLowerCase()} per {fasciaMeta.label.toLowerCase()}
                </p>
                <p className="text-xs text-slate-500 mt-1 mb-5">{dirMeta.desc}</p>
                {canManage && (
                  <Button className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => handleGenera()}>
                    {direzione === "rientro" ? "Genera percorso rientro" : "Genera lista di carico"}
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <CdRouteMap
                  fermate={giro.fermate || []}
                  polyline={giro.polyline}
                  sede={giro.struttura_sede}
                  height="320px"
                />
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Fermate ({giro.fermate?.length || 0})
                  </p>
                  {(giro.fermate || []).length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-6 rounded-lg bg-slate-50">
                      Nessuna fermata nel percorso
                    </p>
                  ) : (
                    (giro.fermate || []).map((f, i) => (
                      <div
                        key={f.fascicolo_id || i}
                        className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
                          {f.ordine || i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800">{f.ospite_nome}</p>
                          <p className="text-xs text-slate-500 truncate">{f.indirizzo}</p>
                        </div>
                        <Badge variant="outline" className="ml-auto text-xs shrink-0">
                          {STATO_FERMATA_LABELS[f.stato_bordo] || f.stato_bordo || "In attesa"}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
